import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Play,
  Clock,
  BookOpen,
  FileText,
  Video,
  Loader2,
  ScrollText,
  Download,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  useLesson,
  useCourse,
  useMarkLessonComplete,
  useLessonProgress,
  useMarkLessonStarted,
  useCoursePrefetch,
  useNextLessonPrefetch,
} from '@/api/hooks';
import { useTrackEntityView, useTrackEntityAction } from '@/hooks/use-entity-tracker';
import { useFileDownload } from '@/hooks/use-file-download';
import { VideoPlayer } from '@/components/media/VideoPlayer';
import { resolveMediaUrl } from '@/lib/media';
import { MediaImage } from '@/components/media';
import { Skeleton } from '@/components/ui/skeleton';
import type { Asset } from '@/components/assets/AssetCard';
import { AssetDetailPanel } from '@/components/assets/AssetDetailPanel';
import { hasAssetSource } from '@/components/assets/asset-source';
import { CourseAssetsDrawer } from '@/components/course-assets/CourseAssetsDrawer';
import { useDrawerHistory } from '@/components/course-assets/useDrawerHistory';
import { NavIconLibrary } from '@/components/icons/NavIcons';

type AssetDrawerState =
  | { kind: 'none' }
  | { kind: 'list' }
  | { kind: 'detail'; asset: Asset };

function assetDrawerLevel(state: AssetDrawerState): number {
  return state.kind === 'none' ? 0 : state.kind === 'list' ? 1 : 2;
}

export const Route = createFileRoute('/learn/courses/$courseId/lessons/$lessonId')({
  component: LessonViewerPage,
});

function LessonViewerPage() {
  const { courseId, lessonId } = Route.useParams();
  const navigate = useNavigate();

  const { data: lesson, isLoading: lessonLoading, error: lessonError } = useLesson(lessonId);
  const { data: course } = useCourse(courseId);
  const trackAction = useTrackEntityAction();
  const { download: downloadFile, isDownloading: isDownloadingTranscript } = useFileDownload();

  const videoMilestonesRef = useRef(new Set<number>());
  useEffect(() => { videoMilestonesRef.current = new Set(); }, [lessonId]);

  const handleVideoProgress = useCallback((pct: number) => {
    for (const milestone of [25, 50, 75, 100]) {
      if (pct >= milestone && !videoMilestonesRef.current.has(milestone)) {
        videoMilestonesRef.current.add(milestone);
        trackAction('progressed', 'lesson', lessonId, lesson?.name ?? '', {
          courseId,
          courseName: course?.name,
          video_milestone_pct: milestone,
        });
      }
    }
  }, [lessonId, lesson, courseId, course, trackAction]);
  const markLessonComplete = useMarkLessonComplete();
  const markLessonStarted = useMarkLessonStarted();

  const visibleLinkedAssets = useMemo(
    () => (lesson?.linked_assets ?? []).filter(hasAssetSource),
    [lesson?.linked_assets],
  );

  const [assetDrawer, setAssetDrawer] = useState<AssetDrawerState>({ kind: 'none' });
  const openCourseAssets = useCallback(() => setAssetDrawer({ kind: 'list' }), []);
  const openAssetDetail = useCallback(
    (asset: Asset) => setAssetDrawer({ kind: 'detail', asset }),
    [],
  );
  const backToAssetList = useCallback(() => setAssetDrawer({ kind: 'list' }), []);
  const closeAssetDrawer = useCallback(() => setAssetDrawer({ kind: 'none' }), []);
  const popAssetDrawer = useCallback(() => {
    setAssetDrawer((current) => {
      if (current.kind === 'detail') return { kind: 'list' };
      if (current.kind === 'list') return { kind: 'none' };
      return current;
    });
  }, []);
  useDrawerHistory(assetDrawerLevel(assetDrawer), popAssetDrawer);

  // Fetch lesson progress for this course
  const { data: lessonProgressData = [] } = useLessonProgress(courseId);

  // Prime browser HTTP cache for fast lesson-to-lesson navigation.
  // - useCoursePrefetch: tier 1 across the whole course (idempotent — coordinator dedupes).
  // - useNextLessonPrefetch: tier 2 (first segment) for the next 1–2 lessons.
  useCoursePrefetch(courseId);
  useNextLessonPrefetch(courseId, lessonId);

  // Find current lesson's progress
  const currentLessonProgress = lessonProgressData.find((lp) => lp.lesson_id === lessonId);

  // Check if lesson is already completed from database
  const [isCompleted, setIsCompleted] = useState(false);

  // Transcript drawer state
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptContent, setTranscriptContent] = useState<string | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState(false);

  // Eagerly load transcript when lesson is available (so we know if it exists before user clicks)
  useEffect(() => {
    if (!lesson) return;
    // Inline text — immediately available
    if (lesson.transcript_text) {
      setTranscriptContent(lesson.transcript_text);
      return;
    }
    // No URL at all — nothing to fetch
    if (!lesson.transcript_url) return;
    let cancelled = false;
    setTranscriptLoading(true);
    setTranscriptError(false);
    setTranscriptContent(null);
    fetch(lesson.transcript_url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setTranscriptContent(text);
      })
      .catch(() => {
        if (!cancelled) setTranscriptError(true);
      })
      .finally(() => {
        if (!cancelled) setTranscriptLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lesson?.id, lesson?.transcript_url, lesson?.transcript_text]);

  // Sync completion state with database
  useEffect(() => {
    if (currentLessonProgress?.status === 'completed') {
      setIsCompleted(true);
    } else {
      setIsCompleted(false);
    }
  }, [currentLessonProgress?.status]);

  // Auto-start lesson when page loads if not already started
  useEffect(() => {
    if (lesson && (!currentLessonProgress || currentLessonProgress.status === 'not_started')) {
      // Use mutate (not mutateAsync) — fire-and-forget, errors must not crash the page
      markLessonStarted.mutate({ lessonId, courseId }, {
        onError: (error) => {
          console.error('Failed to mark lesson as started:', error);
        },
      });
    }
  }, [lessonId, courseId, lesson, currentLessonProgress?.status]);

  // Track lesson view
  useTrackEntityView({
    entityType: 'lesson',
    entityId: lessonId,
    entityName: lesson?.name || 'Untitled Lesson',
    metadata: {
      courseId,
      courseName: course?.name,
      duration_minutes: lesson?.duration_minutes,
      estimatedMinutes: lesson?.estimated_minutes,
      hasVideo: !!lesson?.video_url,
      hasTranscript: !!lesson?.transcript_url,
    },
  });

  // Asset-based video (self-hosted HLS via R2) takes priority over legacy embed URLs
  const hasAssetVideo = !!lesson?.video_asset_id;

  // Find current lesson position in course
  const allLessons =
    course?.modules?.flatMap((m) =>
      (m.lessons || []).map((l) => ({ ...l, moduleName: m.name }))
    ) || [];
  const currentIndex = allLessons.findIndex((l) => l.id === lessonId);
  const previousLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  const handlePrevious = () => {
    if (previousLesson) {
      navigate({
        to: '/learn/courses/$courseId/lessons/$lessonId',
        params: { courseId, lessonId: previousLesson.id },
      });
    }
  };

  const handleNext = () => {
    if (nextLesson) {
      navigate({
        to: '/learn/courses/$courseId/lessons/$lessonId',
        params: { courseId, lessonId: nextLesson.id },
      });
    }
  };

  const handleMarkComplete = async () => {
    // Mark lesson as complete in database
    try {
      await markLessonComplete.mutateAsync({ lessonId, courseId, lessonName: lesson?.name || 'Lesson' });
    } catch (error) {
      console.error('Failed to mark lesson complete:', error);
    }

    // Log lesson completion
    trackAction('completed', 'lesson', lessonId, lesson?.name || 'Untitled Lesson', {
      courseId,
      courseName: course?.name,
      duration_minutes: lesson?.duration_minutes,
      estimatedMinutes: lesson?.estimated_minutes,
    });

    setIsCompleted(true);
    // After a brief delay, go to next lesson or back to course
    setTimeout(() => {
      if (nextLesson) {
        handleNext();
      } else {
        navigate({ to: '/learn/courses/$courseId', params: { courseId } });
      }
    }, 1500);
  };

  if (lessonLoading) {
    return (
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-[400px] w-full rounded-xl" />
        <div className="space-y-3">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (lessonError || !lesson) {
    return (
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/learn/courses/$courseId"
          params={{ courseId }}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Course
        </Link>
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">Lesson Not Found</h2>
          <p className="text-muted-foreground">
            The lesson you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  const hasLegacyEmbed = !!lesson.video_url && !hasAssetVideo;
  const isVideo = hasAssetVideo || hasLegacyEmbed;

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/learn/courses/$courseId"
          params={{ courseId }}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Course
        </Link>

        {/* Lesson navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevious}
            disabled={!previousLesson}
            className="p-2 rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={previousLesson ? `Previous: ${previousLesson.name}` : 'No previous lesson'}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-muted-foreground">
            {currentIndex + 1} / {allLessons.length}
          </span>
          <button
            onClick={handleNext}
            disabled={!nextLesson}
            className="p-2 rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={nextLesson ? `Next: ${nextLesson.name}` : 'No next lesson'}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="grid lg:grid-cols-3 gap-6 min-w-0">
        {/* Lesson content — min-w-0 lets the grid child shrink below its
            intrinsic content width (default min-width:auto otherwise pushes
            wide media past the column edge on mobile). */}
        <div className="lg:col-span-2 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl border border-border overflow-hidden"
          >
            {/* Lesson poster (shown when no video) */}
            {!isVideo && (lesson.media?.poster_url || lesson.media?.small_poster_url) && (
              <div className="h-48">
                <MediaImage
                  src={resolveMediaUrl(lesson.media?.small_poster_url ?? lesson.media?.poster_url)}
                  blurhash={lesson.media?.blurhash}
                  alt={lesson.name}
                  className="w-full h-full object-cover"
                  fallback={<BookOpen className="w-12 h-12 text-white/60 m-auto" />}
                />
              </div>
            )}

            {/* Asset-based video player (self-hosted HLS via R2 / Supabase fallback) */}
            {hasAssetVideo && (
              <VideoPlayer
                assetId={lesson.video_asset_id!}
                filename={lesson.name}
                onProgress={handleVideoProgress}
                onComplete={handleMarkComplete}
                className="rounded-none"
              />
            )}

            {/* Legacy embed (YouTube / Vimeo iframe) — only when no asset video */}
            {hasLegacyEmbed && lesson.video_url && (
              <div className="aspect-video bg-black relative">
                <iframe
                  src={lesson.video_url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            {/* Lesson header */}
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isVideo ? 'bg-red-500/20 text-red-500' : 'bg-indigo-500/20 text-indigo-500'
                  }`}
                >
                  {isVideo ? <Video className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">{lesson.name}</h1>
                  <div className="flex items-center gap-3 mt-1">
                    {(lesson.estimated_minutes || lesson.duration_minutes) && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>approx {lesson.estimated_minutes ?? lesson.duration_minutes} min</span>
                      </div>
                    )}
                    {transcriptContent ? (
                      <button
                        onClick={() => setTranscriptOpen(true)}
                        className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                      >
                        <ScrollText className="w-4 h-4" />
                        <span>Transcript</span>
                      </button>
                    ) : (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground/50 cursor-default">
                        <ScrollText className="w-4 h-4" />
                        <span>Transcript in progress</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {lesson.description && (
                <p className="text-muted-foreground mb-6">{lesson.description}</p>
              )}

              {/* Progress bar for in-progress lessons */}
              {currentLessonProgress &&
                currentLessonProgress.progress_percentage > 0 &&
                currentLessonProgress.progress_percentage < 100 && (
                  <div className="mb-6 p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Lesson Progress</span>
                      <span className="font-semibold text-foreground">
                        {currentLessonProgress.progress_percentage}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-600"
                        initial={{ width: 0 }}
                        animate={{ width: `${currentLessonProgress.progress_percentage}%` }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                      />
                    </div>
                    {currentLessonProgress.time_spent_seconds > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Time spent: {Math.floor(currentLessonProgress.time_spent_seconds / 60)} minutes
                      </p>
                    )}
                  </div>
                )}

              {/* Content area for non-video lessons */}
              {!isVideo && (lesson.content || lesson.transcript_text) && (
                <div className="prose prose-invert max-w-none mb-6 break-words [&_img]:max-w-full [&_img]:h-auto [&_video]:max-w-full [&_iframe]:max-w-full [&_pre]:overflow-x-auto [&_table]:block [&_table]:overflow-x-auto">
                  {lesson.content ? (
                    <div dangerouslySetInnerHTML={{ __html: lesson.content }} />
                  ) : (
                    <div className="whitespace-pre-wrap">{lesson.transcript_text}</div>
                  )}
                </div>
              )}

              {/* Transcript Sheet — opened via button next to duration */}
              {(lesson.transcript_url || lesson.transcript_text) && (
                <Sheet open={transcriptOpen} onOpenChange={setTranscriptOpen}>
                  <SheetContent side="right" className="w-[480px] sm:w-[540px] max-w-full flex flex-col">
                    <SheetHeader className="flex-shrink-0">
                      <SheetTitle className="flex items-center gap-2">
                        <ScrollText className="w-5 h-5 text-indigo-400" />
                        Transcript
                      </SheetTitle>
                      <p className="text-sm text-muted-foreground">{lesson.name}</p>
                    </SheetHeader>

                    {/* Download button */}
                    {(transcriptContent || lesson.transcript_url) && (
                      <div className="flex-shrink-0 pt-2 pb-4 border-b border-border">
                        <button
                          onClick={() =>
                            downloadFile({
                              text: transcriptContent ?? undefined,
                              url: transcriptContent ? undefined : resolveMediaUrl(lesson.transcript_url)!,
                              filename: `${lesson.name} - Transcript.txt`,
                            })
                          }
                          disabled={isDownloadingTranscript || (transcriptError && !transcriptContent)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-500/10 disabled:hover:text-indigo-400"
                        >
                          {isDownloadingTranscript ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          {isDownloadingTranscript ? 'Downloading…' : 'Download transcript'}
                        </button>
                      </div>
                    )}

                    {/* Transcript text — lazy loaded */}
                    <div className="flex-1 overflow-y-auto pt-4">
                      {transcriptLoading ? (
                        <div className="flex items-center justify-center h-full gap-2 text-muted-foreground">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm">Loading transcript…</span>
                        </div>
                      ) : transcriptError ? (
                        <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground gap-2">
                          <FileText className="w-8 h-8 opacity-40" />
                          <p className="text-sm">Transcript not available.</p>
                        </div>
                      ) : transcriptContent ? (
                        <div className="whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed">
                          {transcriptContent}
                        </div>
                      ) : null}
                    </div>
                  </SheetContent>
                </Sheet>
              )}
            </div>
          </motion.div>

          {/* Mark as complete button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-6"
          >
            {isCompleted ? (
              <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 flex items-center justify-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
                <span className="text-green-400 font-medium">
                  Lesson completed! {nextLesson ? 'Moving to next lesson...' : 'Returning to course...'}
                </span>
              </div>
            ) : (
              <button
                onClick={handleMarkComplete}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-5 h-5" />
                Mark as Complete
              </button>
            )}
          </motion.div>
        </div>

        {/* Sidebar - Course outline */}
        <div className="lg:col-span-1 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-xl border border-border p-4 sticky top-4"
          >
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Course Outline
            </h3>

            {visibleLinkedAssets.length > 0 && (
              <button
                type="button"
                onClick={openCourseAssets}
                className="w-full mb-4 px-3 py-2 rounded-lg border border-border hover:bg-secondary transition-colors flex items-center gap-2 text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
              >
                <NavIconLibrary className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium">
                  {visibleLinkedAssets.length} lesson asset
                  {visibleLinkedAssets.length === 1 ? '' : 's'}
                </span>
              </button>
            )}

            {course?.modules?.map((module, moduleIdx) => (
              <div key={module.id} className="mb-4">
                <p className="text-xs text-muted-foreground mb-2">Module {moduleIdx + 1}</p>
                <p className="text-sm font-medium text-foreground mb-2">{module.name}</p>
                <div className="space-y-1 ml-2">
                  {module.lessons?.map((l) => {
                    const isCurrent = l.id === lessonId;
                    const lessonProgress = lessonProgressData.find((lp) => lp.lesson_id === l.id);
                    const isCompleted = lessonProgress?.status === 'completed';
                    return (
                      <button
                        key={l.id}
                        onClick={() =>
                          navigate({
                            to: '/learn/courses/$courseId/lessons/$lessonId',
                            params: { courseId, lessonId: l.id },
                          })
                        }
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          isCurrent
                            ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                            : 'hover:bg-secondary text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {isCurrent ? (
                            <Play className="w-3 h-3 flex-shrink-0" />
                          ) : isCompleted ? (
                            <CheckCircle2 className="w-3 h-3 flex-shrink-0 text-green-500" />
                          ) : (
                            <div className="w-3 h-3 rounded-full border border-current flex-shrink-0" />
                          )}
                          <span className="truncate">{l.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8 flex items-center justify-between"
      >
        {previousLesson ? (
          <button
            onClick={handlePrevious}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <div className="text-left">
              <p className="text-xs">Previous</p>
              <p className="text-sm font-medium">{previousLesson.name}</p>
            </div>
          </button>
        ) : (
          <div />
        )}

        {nextLesson ? (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <div className="text-right">
              <p className="text-xs">Next</p>
              <p className="text-sm font-medium">{nextLesson.name}</p>
            </div>
            <ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <Link
            to="/learn/courses/$courseId"
            params={{ courseId }}
            className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            <div className="text-right">
              <p className="text-xs">Finished</p>
              <p className="text-sm font-medium">Return to Course</p>
            </div>
            <ChevronRight className="w-5 h-5" />
          </Link>
        )}
      </motion.div>

      <CourseAssetsDrawer
        assets={visibleLinkedAssets}
        isOpen={assetDrawer.kind === 'list'}
        onClose={closeAssetDrawer}
        onAssetClick={openAssetDetail}
      />
      <AssetDetailPanel
        asset={assetDrawer.kind === 'detail' ? assetDrawer.asset : null}
        isOpen={assetDrawer.kind === 'detail'}
        onClose={closeAssetDrawer}
        onBack={backToAssetList}
        backLabel="Back to course assets"
      />
    </div>
  );
}
